import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { projects } from "../../data/portfolio";
import { Sparkles, FlaskConical } from "lucide-react";

const filters = [
  { id: "all", label: "All" },
  { id: "real", label: "Delivered" },
  { id: "lab", label: "AI Lab" },
];

export const Projects = () => {
  const [filter, setFilter] = useState("all");

  const list = useMemo(
    () => (filter === "all" ? projects : projects.filter((p) => p.kind === filter)),
    [filter]
  );

  return (
    <section id="projects" data-testid="projects-section" className="relative py-24 md:py-32 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <div className="text-xs font-mono-display uppercase tracking-widest text-[color:var(--primary)] mb-3">
              /04 · Portfolio
            </div>
            <h2 className="font-mono-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold">
              Real shipped work + <span className="text-gradient-neon">AI lab</span> experiments.
            </h2>
            <p className="text-white/60 mt-4 max-w-2xl font-body">
              Below is a mix of production-delivered projects from my career and self-built
              GenAI / Agentic AI showcases. Lab projects are clearly tagged.
            </p>
          </div>

          <div className="flex gap-2 flex-wrap" data-testid="projects-filters">
            {filters.map((f) => (
              <button
                key={f.id}
                data-testid={`projects-filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={`px-4 py-2 rounded-full font-mono-display text-xs uppercase tracking-widest border transition-all ${
                  filter === f.id
                    ? "bg-[color:var(--primary)] text-black border-[color:var(--primary)] glow-cyan"
                    : "border-white/20 text-white/70 hover:border-white/50"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {list.map((p, i) => (
            <motion.article
              key={p.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              data-testid={`project-card-${p.title.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
              className="tilt-card glass rounded-2xl overflow-hidden hover:border-[color:var(--secondary)]/40 group"
            >
              <div className="relative h-44 overflow-hidden">
                <img
                  src={p.image}
                  alt={p.title}
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute top-3 left-3 flex gap-2">
                  <span className={`inline-flex items-center gap-1 text-[10px] font-mono-display uppercase tracking-widest px-2 py-1 rounded-full border ${
                    p.kind === "real"
                      ? "border-[color:var(--primary)]/50 text-[color:var(--primary)] bg-black/50"
                      : "border-[color:var(--accent)]/50 text-[color:var(--accent)] bg-black/50"
                  }`}>
                    {p.kind === "real" ? <Sparkles size={10} /> : <FlaskConical size={10} />}
                    {p.kind === "real" ? "Delivered" : "AI Lab"}
                  </span>
                </div>
                <div className="absolute bottom-3 left-3 right-3">
                  <div className="text-xs font-mono-display text-[color:var(--accent)] uppercase tracking-widest">
                    {p.tag}
                  </div>
                </div>
              </div>

              <div className="p-5">
                <h3 className="font-mono-display text-lg text-white leading-snug">{p.title}</h3>
                <p className="mt-2 text-sm text-white/65 font-body leading-relaxed">
                  {p.description}
                </p>

                <div className="mt-4 flex flex-wrap gap-1.5">
                  {p.stack.map((s) => (
                    <span
                      key={s}
                      className="text-[10px] font-mono-display uppercase tracking-widest px-2 py-1 rounded-full border border-white/10 text-white/70"
                    >
                      {s}
                    </span>
                  ))}
                </div>

                {p.impact && (
                  <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap gap-3">
                    {p.impact.map((im) => (
                      <span key={im} className="text-xs text-white/60 font-body">
                        <span className="text-[color:var(--accent)] mr-1">●</span>
                        {im}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;